// "Step 2 of 2" — the survey as the next step of registering, not an
// afterthought to a "You're registered" page (Michael, 10 Oct 2026,
// mca_546f5fd509fe; thread mct_13c797d3-644).
//
// ★ Why this exists: on The Firm That Runs Without You only 91 of 176
// registrants finished the survey, and the drop-off was the same before and
// after the 24 Sep blank-question fix. The screen after registering opened
// with "You're in" and a calendar band; on a phone the survey button sat
// ~2,700px down. It read as finished, so people left. With `gatePage.stepTwo`
// set, someone whose seat is already saved lands straight on the first
// question, with "seat saved" as one quiet line above it.
//
// ★ Only for a seat that is ACTUALLY saved. The on-page registration form
// (/api/embed/register) writes the submission before the survey, so a
// resume-token arrival in 'registered-pending-survey' really has a seat. The
// in-page email gate saves nothing until the survey is submitted, so it keeps
// the gate page — telling that person "seat saved" would be untrue.
import { interpolateBlocks } from './interpolate.ts';
import type { ContentBlock, GatePage, RegistrationStatus } from '../types';

export const STEP_TWO_DEFAULTS = {
  seatSavedLine: 'Seat saved for {{eventDayLong}}, {{eventTimeShort}}',
  seatSavedLineNoDate: 'Seat saved',
  stepLabel: 'Step 2 of 2',
  skipLabel: 'Skip for now, my seat is saved',
} as const;

/** The event template vars the gate page and step two both interpolate.
 *  {{greeting}} carries its own leading comma ("You're in{{greeting}}."). */
export const EVENT_VAR_KEYS = [
  'eventDate', 'eventTimezone',
  'eventDayLong', 'eventTimeShort', 'eventTimeRange', 'eventDateTimeFull',
  'eventDuration', 'eventDateOrdinal', 'eventWeekday', 'eventDayPart', 'eventDayRelative', 'eventDayRelativeLower',
  'replayClosesShort', 'replayClosesDay', 'replayClosesDate',
  'replayClosesOrdinal', 'replayClosesRelative', 'replayClosesDateTimeFull',
] as const;

export function buildEventVars(
  eventConfig: Record<string, unknown> | undefined,
  leadFirstName: string | null | undefined,
): Record<string, string> {
  const first = leadFirstName ? leadFirstName.split(' ')[0] : '';
  const vars: Record<string, string> = { firstName: first, greeting: first ? `, ${first}` : '' };
  if (eventConfig) {
    for (const k of EVENT_VAR_KEYS) {
      const v = eventConfig[k];
      if (typeof v === 'string' && v) vars[k] = v;
    }
  }
  return vars;
}

/** Does this visitor land straight on the questions as step two? */
export function stepTwoApplies(
  gatePage: GatePage | undefined,
  status: RegistrationStatus | null | undefined,
  previewActive = false,
): boolean {
  if (!gatePage?.stepTwo) return false;
  if (status === 'registered-pending-survey') return true;
  // Admin preview has no real registration; show the step-two header on the
  // questions so the page can be checked before an event is published.
  return previewActive && (status === null || status === undefined);
}

export interface StepTwoCopy {
  seatSaved: string;
  stepLabel: string;
  intro: string | null;
  skipLabel: string;
  belowBlocks: ContentBlock[];
}

/** Resolve step-two copy with event vars. A template whose date vars are
 *  missing falls back to a plain "Seat saved" rather than "Seat saved for , ". */
export function resolveStepTwoCopy(gatePage: GatePage, vars: Record<string, string>): StepTwoCopy {
  const st = gatePage.stepTwo ?? {};
  const fill = (s: string) => s.replace(/\{\{(\w+)\}\}/g, (_, k: string) => vars[k] ?? '');
  const seatTemplate = st.seatSavedLine ?? STEP_TWO_DEFAULTS.seatSavedLine;
  const missingVar = [...seatTemplate.matchAll(/\{\{(\w+)\}\}/g)].some((m) => !vars[m[1]]);
  return {
    seatSaved: missingVar ? STEP_TWO_DEFAULTS.seatSavedLineNoDate : fill(seatTemplate),
    stepLabel: fill(st.stepLabel ?? STEP_TWO_DEFAULTS.stepLabel),
    intro: st.intro ? fill(st.intro) : null,
    skipLabel: fill(st.skipLabel ?? STEP_TWO_DEFAULTS.skipLabel),
    belowBlocks: st.belowBlocks?.length ? interpolateBlocks(st.belowBlocks, vars) : [],
  };
}
